import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowRight, MessageSquare, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SuggestedQuestion {
  id: string;
  text: string;
  category: string;
}

interface SuggestedQuestionsV3Props {
  language: 'ru' | 'en';
  theme?: 'dark' | 'light';
  onQuestionSelect?: (question: string) => void;
  suggestedQuestions?: any; // Данные от сервера
  isTransitioning?: boolean;
  transitionDelay?: number;
}

export function SuggestedQuestionsV3({ 
  language, 
  theme = 'light',
  onQuestionSelect,
  suggestedQuestions,
  isTransitioning = false,
  transitionDelay = 0
}: SuggestedQuestionsV3Props) {
  const { t } = useTranslation('analysis');
  const isDark = theme === 'dark';

  // Используем данные от сервера, если они есть, иначе fallback на фиксированные вопросы
  const questionsToShow = suggestedQuestions?.questions || (language === 'ru' ? [
    'Стоит ли мне менять работу в 35 лет?',
    'Стоит ли покупать квартиру в ипотеку сейчас?',
    'Стоит ли переезжать в другой город ради карьеры?',
    'Стоит ли открывать собственный бизнес в 30 лет?',
    'Стоит ли получать MBA сейчас?',
    'Стоит ли инвестировать в акции при нестабильности?',
    'Стоит ли заводить детей, если нет своей квартиры?',
    'Стоит ли уволиться без предложения о работе?'
  ] : [
    'Should I change jobs at 35?',
    'Should I buy a house with a mortgage now?',
    'Should I move to another city for career?',
    'Should I start my own business at 30?',
    'Should I get an MBA now?',
    'Should I invest in stocks during instability?',
    'Should I have children without owning a home?',
    'Should I quit without a job offer?'
  ]);

  const handleQuestionClick = (question: string) => {
    if (onQuestionSelect) {
      onQuestionSelect(question);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDark 
              ? 'bg-blue-500/20 border border-blue-500/30'
              : 'bg-blue-50 border border-blue-200/60'
          }`}>
            <Sparkles className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h3 className={`text-xl font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {language === 'ru' ? 'Попробуйте эти вопросы' : 'Try These Questions'}
            </h3>
            <p className={`text-sm ${
              isDark ? 'text-slate-400' : 'text-gray-600'
            }`}>
              {t('popularQuestions')}
            </p>
          </div>
        </div>
      </div>

      {/* Stories-style horizontal scroll */}
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto py-2 pb-6 scrollbar-hide scroll-smooth">
          <div className="flex gap-4 px-1">
            {questionsToShow.map((question, index) => (
              <Card
                key={index}
                className={`flex-shrink-0 w-80 p-5 cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:-translate-y-1 group ${
                  isDark 
                    ? 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600/70 hover:bg-slate-800/50' 
                    : 'bg-white/80 border-gray-200/60 hover:border-gray-300/80 hover:bg-white shadow-sm hover:shadow-xl backdrop-blur-sm'
                }`}
                onClick={() => handleQuestionClick(question)}
              >
                <div>
                <div className="space-y-4">
                  {/* Question text */}
                  <p className={`text-lg leading-relaxed font-medium ${
                    isDark ? 'text-slate-100' : 'text-gray-900'
                  }`}>
                    {question}
                  </p>
                  
                  {/* Action indicator */}
                  <div className="flex items-center justify-between pt-2">
                    <span className={`text-xs ${
                      isDark ? 'text-slate-400' : 'text-gray-500'
                    }`}>
                      {language === 'ru' ? 'Нажмите для анализа' : 'Click to analyze'}
                    </span>
                    <ArrowRight className={`w-4 h-4 transition-all group-hover:translate-x-1 ${
                      isDark ? 'text-slate-400 group-hover:text-blue-400' : 'text-gray-400 group-hover:text-blue-600'
                    }`} />
                  </div>
                </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Gradient fades on both edges */}
        <div className={`absolute top-0 left-0 w-6 h-full pointer-events-none bg-gradient-to-r z-10 ${
          isDark 
            ? 'from-slate-900 to-transparent'
            : 'from-white to-transparent'
        }`} />
        <div className={`absolute top-0 right-0 w-12 h-full pointer-events-none bg-gradient-to-l z-10 ${
          isDark 
            ? 'from-slate-900 to-transparent'
            : 'from-white to-transparent'
        }`} />
      </div>
      
      {/* Mobile scroll hint */}
      <div className={`text-center text-xs ${
        isDark ? 'text-slate-500' : 'text-gray-400'
      } md:hidden`}>
        {t('swipeForMore')}
      </div>
    </div>
  );
}
